// Location: frontend/src/admin/pages/ManageBlogs.jsx
// Refactored to Tailwind CSS

import React, { useEffect, useState } from 'react';
import * as MdIcons from 'react-icons/md';
import * as FaIcons from 'react-icons/fa';
import * as AiIcons from 'react-icons/ai';
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

const ManageBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewBlog, setViewBlog] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  const [blogData, setBlogData] = useState({
    title: "",
    author: "",
    description: "",
    content: "",
    category: "",
    tags: [],
    slug: "",
    metaTitle: "",
    metaDescription: "",
    image: null,
    status: true,
  });

  const [tagInput, setTagInput] = useState('');

  // ============== Fetch Blogs ==============
  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, []);

  // ============== Filter Blogs ==============
  useEffect(() => {
    let filtered = blogs;

    if (searchTerm) {
      filtered = filtered.filter(blog =>
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(blog => blog.category === filterCategory);
    }

    setFilteredBlogs(filtered);
  }, [searchTerm, filterCategory, blogs]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/blogs/admin`, {
        withCredentials: true
      });
      if (res.data.success) {
        setBlogs(res.data.data);
        setFilteredBlogs(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      alert("Failed to fetch blogs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/public/categories`);
      if (res.data.success) {
        setCategories(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // ============== Handle Form Submit ==============
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('title', blogData.title);
    formData.append('author', blogData.author);
    formData.append('description', blogData.description);
    formData.append('content', blogData.content);
    formData.append('category', blogData.category);
    formData.append('tags', JSON.stringify(blogData.tags));
    formData.append('slug', blogData.slug || blogData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
    formData.append('metaTitle', blogData.metaTitle || blogData.title);
    formData.append('metaDescription', blogData.metaDescription || blogData.description);
    formData.append('status', blogData.status);

    if (blogData.image) {
      formData.append('image', blogData.image);
    }

    try {
      if (editingBlog) {
        const res = await axios.put(`${API_URL}/admin/blogs/${editingBlog._id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true
        });
        if (res.data.success) {
          alert("Blog updated successfully!");
          fetchBlogs();
          closeForm();
        }
      } else {
        const res = await axios.post(`${API_URL}/admin/blogs`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true
        });
        if (res.data.success) {
          alert("Blog created successfully!");
          fetchBlogs();
          closeForm();
        }
      }
    } catch (error) {
      console.error("Error saving blog:", error);
      alert(error.response?.data?.message || "Failed to save blog");
    } finally {
      setLoading(false);
    }
  };

  // ============== Delete Blog ==============
  const deleteBlog = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;

    setLoading(true);
    try {
      const res = await axios.delete(`${API_URL}/admin/blogs/${id}`, {
        withCredentials: true
      });
      if (res.data.success) {
        alert("Blog deleted successfully!");
        fetchBlogs();
      }
    } catch (error) {
      console.error("Error deleting blog:", error);
      alert("Failed to delete blog");
    } finally {
      setLoading(false);
    }
  };

  // ============== Toggle Status ==============
  const toggleStatus = async (blog) => {
    // setLoading(true); // Don't block UI for simple toggle
    try {
      const res = await axios.patch(`${API_URL}/admin/blogs/${blog._id}/toggle`, {}, {
        withCredentials: true
      });
      if (res.data.success) {
        fetchBlogs();
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      // Silently fail or show toast if available, avoiding alert for simple toggle
    }
  };

  // ============== Open Form ==============
  const openForm = (blog = null) => {
    if (blog) {
      setEditingBlog(blog);
      setBlogData({
        title: blog.title,
        author: blog.author,
        description: blog.description,
        content: blog.content,
        category: blog.category,
        tags: blog.tags || [],
        slug: blog.slug,
        metaTitle: blog.metaTitle,
        metaDescription: blog.metaDescription,
        image: null,
        status: blog.status,
      });
      setImagePreview(blog.image ? `${API_URL}/uploads/${blog.image}` : null);
    } else {
      setEditingBlog(null);
      setBlogData({
        title: "",
        author: "",
        description: "",
        content: "",
        category: "",
        tags: [],
        slug: "",
        metaTitle: "",
        metaDescription: "",
        image: null,
        status: true,
      });
      setImagePreview(null);
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingBlog(null);
    setImagePreview(null);
    setTagInput('');
  };

  // ============== Handle Image Change ==============
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should not exceed 5MB");
        return;
      }
      setBlogData({ ...blogData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // ============== Handle Tags ==============
  const addTag = () => {
    if (tagInput.trim() && !blogData.tags.includes(tagInput.trim())) {
      setBlogData({ ...blogData, tags: [...blogData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (index) => {
    const newTags = blogData.tags.filter((_, i) => i !== index);
    setBlogData({ ...blogData, tags: newTags });
  };

  // ============== Auto-generate Slug ==============
  const generateSlug = () => {
    const slug = blogData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setBlogData({ ...blogData, slug });
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FaIcons.FaBlog className="text-primary" /> Manage Blogs
          </h2>
          <p className="text-sm text-gray-500 mt-1">Create, edit, and manage your blog posts</p>
        </div>
        <button
          className="flex items-center justify-center px-4 py-2.5 bg-primary text-white rounded-lg hover:shadow-lg hover:bg-primary-dark transition-all duration-200 font-medium"
          onClick={() => openForm()}
        >
          <AiIcons.AiOutlinePlus className="mr-2" />
          Add New Blog
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MdIcons.MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
          <input
            type="text"
            placeholder="Search blogs by title, author, or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
          />
        </div>
        <div className="w-full sm:w-64">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-white"
          >
            <option value="all">All Categories</option>
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Author</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Views</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredBlogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No blogs found matching your criteria.
                  </td>
                </tr>
              ) : (
                filteredBlogs.map((blog) => (
                  <tr key={blog._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 overflow-hidden">
                          {blog.image ? (
                            <img
                              src={`${API_URL}/uploads/${blog.image}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <FaIcons.FaImage />
                            </div>
                          )}
                        </div>
                        <div className="overflow-hidden">
                          <span className="font-medium text-gray-900 truncate max-w-xs block" title={blog.title}>
                            {blog.title}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{blog.author}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {blog.category || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <MdIcons.MdRemoveRedEye className="text-gray-400" /> {blog.views || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleStatus(blog)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${blog.status ? 'bg-green-500' : 'bg-gray-200'}`}
                      >
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${blog.status ? 'translate-x-5' : 'translate-x-0'}`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          onClick={() => setViewBlog(blog)}
                          title="View"
                        >
                          <MdIcons.MdRemoveRedEye />
                        </button>
                        <button
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          onClick={() => openForm(blog)}
                          title="Edit"
                        >
                          <FaIcons.FaEdit />
                        </button>
                        <button
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          onClick={() => deleteBlog(blog._id)}
                          title="Delete"
                        >
                          <MdIcons.MdOutlineDeleteForever />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto my-8 scrollbar-hide">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 sticky top-0 z-10">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                {editingBlog ? <><FaIcons.FaEdit className="text-primary" /> Edit Blog</> : <><AiIcons.AiOutlinePlus className="text-primary" /> Add New Blog</>}
              </h3>
              <button
                onClick={closeForm}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <MdIcons.MdClose className="text-xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={blogData.title}
                    onChange={(e) => setBlogData({ ...blogData, title: e.target.value })}
                    required
                    placeholder="Enter blog title"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Author *</label>
                  <input
                    type="text"
                    value={blogData.author}
                    onChange={(e) => setBlogData({ ...blogData, author: e.target.value })}
                    required
                    placeholder="Enter author name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={blogData.category}
                    onChange={(e) => setBlogData({ ...blogData, category: e.target.value })}
                    placeholder="e.g., Technology, Health"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Slug (URL)</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={blogData.slug}
                      onChange={(e) => setBlogData({ ...blogData, slug: e.target.value })}
                      placeholder="auto-generated-slug"
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={generateSlug}
                      className="px-4 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg hover:bg-gray-200 transition-colors text-sm font-medium text-gray-600"
                    >
                      Generate
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Description (Short summary)</label>
                <textarea
                  value={blogData.description}
                  onChange={(e) => setBlogData({ ...blogData, description: e.target.value })}
                  rows="3"
                  placeholder="Brief description of the blog (max 500 characters)"
                  maxLength="500"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                />
                <div className="text-right text-xs text-gray-500 mt-1">{blogData.description.length}/500</div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Content *</label>
                <textarea
                  value={blogData.content}
                  onChange={(e) => setBlogData({ ...blogData, content: e.target.value })}
                  required
                  rows="10"
                  placeholder="Write your blog content here..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all font-mono text-sm"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tags</label>
                <div className="p-2 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent transition-all">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {blogData.tags.map((tag, idx) => (
                      <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-sm">
                        {tag}
                        <button type="button" onClick={() => removeTag(idx)} className="ml-1.5 text-blue-400 hover:text-blue-600">
                          <MdIcons.MdClose />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="Add tag and press Enter"
                      className="flex-1 outline-none text-sm"
                    />
                    <button type="button" onClick={addTag} className="text-primary hover:text-primary-dark font-medium text-sm">
                      Add
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Featured Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500
                                        file:mr-4 file:py-2 file:px-4
                                        file:rounded-full file:border-0
                                        file:text-sm file:font-semibold
                                        file:bg-blue-50 file:text-blue-700
                                        hover:file:bg-blue-100
                                    "
                />
                {imagePreview && (
                  <div className="mt-4 relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 group">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Max size: 5MB. Formats: JPG, PNG, WEBP</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-100">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Meta Title (SEO)</label>
                  <input
                    type="text"
                    value={blogData.metaTitle}
                    onChange={(e) => setBlogData({ ...blogData, metaTitle: e.target.value })}
                    placeholder="Leave empty to use blog title"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Meta Description (SEO)</label>
                  <input
                    type="text"
                    value={blogData.metaDescription}
                    onChange={(e) => setBlogData({ ...blogData, metaDescription: e.target.value })}
                    placeholder="Leave empty to use description"
                    maxLength="160"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none text-sm"
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">{blogData.metaDescription.length}/160</div>
                </div>
              </div>

              <div className="mb-6">
                <label className="inline-flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={blogData.status}
                      onChange={(e) => setBlogData({ ...blogData, status: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${blogData.status ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${blogData.status ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">Publish immediately</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : (editingBlog ? 'Update Blog' : 'Create Blog')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Blog Modal */}
      {viewBlog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto my-8 relative scrollbar-hide">
            <button
              onClick={() => setViewBlog(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full p-2 shadow-sm hover:shadow-md z-10"
            >
              <MdIcons.MdClose className="text-2xl" />
            </button>

            {viewBlog.image && (
              <div className="w-full h-64 sm:h-80 bg-gray-100 relative">
                <img
                  src={`${API_URL}/uploads/${viewBlog.image}`}
                  alt={viewBlog.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
                  <h2 className="text-3xl font-bold text-white shadow-sm">{viewBlog.title}</h2>
                </div>
              </div>
            )}

            <div className="p-8">
              {!viewBlog.image && (
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{viewBlog.title}</h2>
              )}

              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-8 pb-8 border-b border-gray-100">
                <span className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full"><FaIcons.FaUser className="text-primary" /> {viewBlog.author}</span>
                <span className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full"><FaIcons.FaFolder className="text-primary" /> {viewBlog.category}</span>
                <span className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full"><MdIcons.MdRemoveRedEye className="text-primary" /> {viewBlog.views || 0} views</span>
                <span className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full"><FaIcons.FaClock className="text-primary" /> {new Date(viewBlog.createdAt).toLocaleDateString()}</span>
              </div>

              {viewBlog.tags && viewBlog.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {viewBlog.tags.map((tag, idx) => (
                    <span key={idx} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">#{tag}</span>
                  ))}
                </div>
              )}

              {viewBlog.description && (
                <div className="mb-8 bg-blue-50 p-6 rounded-xl border border-blue-100">
                  <h4 className="font-bold text-blue-900 mb-2">Summary</h4>
                  <p className="text-blue-800 leading-relaxed opacity-90">{viewBlog.description}</p>
                </div>
              )}

              <div className="prose max-w-none text-gray-700 leading-relaxed mb-8">
                <p className="whitespace-pre-wrap">{viewBlog.content}</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 text-sm">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <MdIcons.MdSearch className="text-gray-500" /> SEO Metadata
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="block text-gray-500 text-xs uppercase font-semibold tracking-wider">Slug</span>
                    <span className="font-mono text-gray-700 bg-white px-2 py-1 rounded border border-gray-200 block mt-1">{viewBlog.slug}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500 text-xs uppercase font-semibold tracking-wider">Meta Title</span>
                    <span className="text-gray-700 block mt-1">{viewBlog.metaTitle || 'Not set'}</span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="block text-gray-500 text-xs uppercase font-semibold tracking-wider">Meta Description</span>
                    <span className="text-gray-700 block mt-1">{viewBlog.metaDescription || 'Not set'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBlogs;